/**
 * [BL] Wishlist — hearts that save products to a list kept in this browser (localStorage, no account).
 *
 * - <bl-wishlist-button data-handle> (snippets/bl-wishlist-button.liquid): toggles the product; every
 *   heart for the same product, the header count and other tabs stay in step.
 * - [data-bl-wishlist-count]: shows how many are saved (hidden at 0).
 * - <bl-wishlist-list>: the saved list, in the wishlist drawer (snippets/bl-wishlist-drawer.liquid,
 *   opened by the header heart; cards load when it is first opened) and on the optional saved page
 *   (sections/bl-wishlist.liquid). Each product's card is fetched
 *   from the store (`/products/<handle>?section_id=bl-wishlist-card`), so prices and photos are current.
 *
 * The list is an array of product handles, newest first. It lives on this device only: it doesn't
 * follow the shopper to another browser, and clearing site data clears it.
 */

const KEY = 'bl-wishlist';
const MAX = 100;

/** @returns {string[]} */
function read() {
  try {
    const parsed = JSON.parse(localStorage.getItem(KEY) || '[]');
    return Array.isArray(parsed) ? parsed.filter((handle) => typeof handle === 'string' && handle) : [];
  } catch {
    return [];
  }
}

/** @param {string[]} handles */
function write(handles) {
  try {
    localStorage.setItem(KEY, JSON.stringify(handles.slice(0, MAX)));
  } catch {
    // Storage blocked (private mode): the hearts still toggle for this page view.
  }
  memory = handles.slice(0, MAX);
  document.dispatchEvent(new CustomEvent('bl-wishlist:change', { detail: { handles: memory } }));
}

/** In-memory copy, so a blocked localStorage still works within the page. */
let memory = read();

const wishlist = {
  list: () => [...memory],
  has: (/** @type {string} */ handle) => memory.includes(handle),
  /**
   * @param {string} handle
   * @param {Record<string, string | undefined>} [product] - What the heart knows about the product
   *   (title, productId, variantId, price in cents), passed on with `bl-wishlist:added` for tracking.
   * @returns {boolean} Whether it is saved now.
   */
  toggle(handle, product = {}) {
    if (!handle) return false;
    const saved = memory.includes(handle);
    write(saved ? memory.filter((entry) => entry !== handle) : [handle, ...memory]);
    // assets/bl-tracking.js turns this into the `bl_saved_product` custom event; the header dot pulses.
    if (!saved) document.dispatchEvent(new CustomEvent('bl-wishlist:added', { detail: { handle, product } }));
    return !saved;
  },
  remove(/** @type {string} */ handle) {
    if (memory.includes(handle)) write(memory.filter((entry) => entry !== handle));
  },
};

/** @type {any} */ (window).BLWishlist = wishlist;

// Another tab changed the list.
window.addEventListener('storage', (event) => {
  if (event.key !== KEY) return;
  memory = read();
  document.dispatchEvent(new CustomEvent('bl-wishlist:change', { detail: { handles: memory } }));
});

function renderCounts() {
  for (const element of document.querySelectorAll('[data-bl-wishlist-count]')) {
    element.textContent = String(memory.length > 99 ? '99+' : memory.length);
    /** @type {HTMLElement} */ (element).hidden = memory.length === 0;
    // The header heart's bubble is the cart's bubble: hide the whole thing at 0, and let the icon
    // cut its notch only when the bubble is there.
    const bubble = /** @type {HTMLElement | null} */ (element.closest('[data-bl-wishlist-bubble]'));
    if (bubble) bubble.hidden = memory.length === 0;
    element
      .closest('[data-bl-wishlist-icon]')
      ?.classList.toggle('bl-wishlist-icon--has-count', memory.length > 0);
  }
}
document.addEventListener('bl-wishlist:change', renderCounts);
renderCounts();

// A product was just saved: the header dot pops and sends out a ring (restarted on every save).
document.addEventListener('bl-wishlist:added', () => {
  for (const bubble of document.querySelectorAll('[data-bl-wishlist-bubble]')) {
    bubble.classList.remove('bl-wishlist-dot--pulse');
    void (/** @type {HTMLElement} */ (bubble).offsetWidth);
    bubble.classList.add('bl-wishlist-dot--pulse');
  }
});

class WishlistButton extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button');
    this.addEventListener('click', this.#onClick);
    document.addEventListener('bl-wishlist:change', this.#sync);
    this.#sync();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    document.removeEventListener('bl-wishlist:change', this.#sync);
  }

  /** @param {Event} event */
  #onClick = (event) => {
    // A heart inside a product card's link must not open the product.
    event.preventDefault();
    event.stopPropagation();
    const saved = wishlist.toggle(this.dataset.handle || '', {
      title: this.dataset.title,
      productId: this.dataset.productId,
      variantId: this.dataset.variantId,
      price: this.dataset.price,
    });
    if (saved) {
      this.classList.remove('bl-wishlist-button--pop');
      void this.offsetWidth;
      this.classList.add('bl-wishlist-button--pop');
    }
  };

  #sync = () => {
    const saved = wishlist.has(this.dataset.handle || '');
    this.toggleAttribute('data-saved', saved);
    if (!this.button) return;
    this.button.setAttribute('aria-pressed', String(saved));
    const label = saved ? this.button.dataset.savedLabel : this.button.dataset.saveLabel;
    this.button.setAttribute('aria-label', `${label || ''} ${this.dataset.title || ''}`.trim());
  };
}

if (!customElements.get('bl-wishlist-button')) customElements.define('bl-wishlist-button', WishlistButton);

class WishlistList extends HTMLElement {
  /** Handles whose cards are in the grid. @type {Set<string>} */
  #rendered = new Set();
  /** Whether cards are being loaded yet (a list in the drawer waits until it is opened). */
  #active = false;

  connectedCallback() {
    this.grid = /** @type {HTMLElement} */ (this.querySelector('[data-bl-wishlist-grid]'));
    this.empty = /** @type {HTMLElement} */ (this.querySelector('[data-bl-wishlist-empty]'));
    document.addEventListener('bl-wishlist:change', this.#onChange);
    if (this.hasAttribute('data-lazy')) {
      // In the drawer: don't fetch the cards on every page, only once the drawer is on screen.
      this.#toggleEmpty(wishlist.list().length === 0);
      this.observer = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.observer?.disconnect();
        this.#active = true;
        this.#load();
      });
      this.observer.observe(this);
    } else {
      this.#active = true;
      this.#load();
    }
  }

  disconnectedCallback() {
    document.removeEventListener('bl-wishlist:change', this.#onChange);
    this.observer?.disconnect();
  }

  // Un-saving on this page takes the card away; saving elsewhere (another tab) adds it.
  #onChange = () => {
    for (const card of Array.from(this.grid?.children || [])) {
      const handle = /** @type {HTMLElement} */ (card).dataset.handle || '';
      if (!wishlist.has(handle)) {
        card.remove();
        this.#rendered.delete(handle);
      }
    }
    if (this.#active) this.#load();
    else this.#toggleEmpty(wishlist.list().length === 0);
  };

  async #load() {
    const handles = wishlist.list();
    this.#toggleEmpty(handles.length === 0);
    const root = /** @type {any} */ (window).Shopify?.routes?.root || '/';
    const missing = handles.filter((handle) => !this.#rendered.has(handle));
    for (const handle of missing) this.#rendered.add(handle);

    // A few at a time, so a long list doesn't fire every request at once.
    /** @type {{ handle: string, card: Element | null, gone: boolean }[]} */
    const cards = [];
    for (let start = 0; start < missing.length; start += 4) {
      const batch = await Promise.all(
        missing.slice(start, start + 4).map(async (handle) => {
          try {
            const response = await fetch(`${root}products/${encodeURIComponent(handle)}?section_id=bl-wishlist-card`);
            // Only a product that no longer exists leaves the list; any other failure keeps it saved.
            if (response.status === 404) return { handle, card: null, gone: true };
            if (!response.ok) return { handle, card: null, gone: false };
            const html = new DOMParser().parseFromString(await response.text(), 'text/html');
            const card = html.querySelector('[data-bl-wishlist-card]');
            return { handle, card, gone: !card };
          } catch {
            return { handle, card: null, gone: false };
          }
        })
      );
      cards.push(...batch);
    }

    for (const { handle, card, gone } of cards) {
      if (!card) {
        this.#rendered.delete(handle); // tried again the next time the list loads
        if (gone) wishlist.remove(handle);
        continue;
      }
      if (!wishlist.has(handle)) {
        // Un-saved while loading: forget it, so saving it again shows it.
        this.#rendered.delete(handle);
        continue;
      }
      /** @type {HTMLElement} */ (card).dataset.handle = handle;
      this.grid?.append(document.importNode(card, true));
    }
    // Keep the saved order (newest first).
    const order = wishlist.list();
    const sorted = Array.from(this.grid?.children || []).sort(
      (a, b) =>
        order.indexOf(/** @type {HTMLElement} */ (a).dataset.handle || '') -
        order.indexOf(/** @type {HTMLElement} */ (b).dataset.handle || '')
    );
    this.grid?.replaceChildren(...sorted);
    this.toggleAttribute('data-loaded', true);
    this.#toggleEmpty(wishlist.list().length === 0);
  }

  #toggleEmpty(/** @type {boolean} */ isEmpty) {
    if (this.empty) this.empty.hidden = !isEmpty;
    if (this.grid) this.grid.hidden = isEmpty;
  }
}

if (!customElements.get('bl-wishlist-list')) customElements.define('bl-wishlist-list', WishlistList);
